import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { Search, Edit2, Trash2, Plus, Loader, AlertCircle, X, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Users, DollarSign, Eye, Printer, CreditCard, Calendar, Phone, MapPin, Building, Clock, FileSpreadsheet } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm disabled:bg-gray-50";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide";

const Employees = () => {
  const abortControllerRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // HR Stats
  const [hrStats, setHrStats] = useState({
    total_employees: 0, active_employees: 0, inactive_employees: 0,
    total_monthly_payroll: '0', paid_this_month: 0, unpaid_this_month: 0,
    advance_outstanding: '0', pending_attendance_summaries: 0, latest_bonus_pool: null,
  });

  const fetchEmployees = useCallback(async (page = 1) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', pageSize);
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (statusFilter) params.set('is_active', statusFilter);

      const res = await api.get(`/employees/?${params.toString()}`, {
        signal: abortControllerRef.current.signal,
      });

      setEmployees(res.data.results || []);
      setTotalCount(res.data.count || 0);
      setNextUrl(res.data.next);
      setPrevUrl(res.data.previous);
      setCurrentPage(page);
    } catch (err) {
      if (err.code !== 'ERR_CANCELED') {
        console.error("Error fetching employees:", err);
        setError("Failed to load employees. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, statusFilter, pageSize]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/employees/hr-stats/');
      setHrStats({
        ...res.data,
      });
    } catch (err) {
      // silent fail for stats
    }
  }, []);

  useEffect(() => {
    fetchEmployees(1);
  }, [fetchEmployees]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/employees/${id}/`);
      setDeleteConfirm(null);
      fetchEmployees(currentPage);
      fetchStats();
    } catch (err) {
      console.error("Error deleting employee:", err);
      alert("Failed to delete employee.");
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
  };

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    return `PKR ${amount.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const hasActiveFilters = searchTerm || statusFilter;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) fetchEmployees(page);
  };

  if (loading && employees.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading employees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
        </div>
      )}

      {/* HR Summary Dashboard */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="card p-3 flex flex-col gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100">
            <Users className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase leading-tight">Total Staff</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{hrStats.total_employees}</p>
          </div>
        </div>
        <div className="card p-3 flex flex-col gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-100">
            <Users className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase leading-tight">Active</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{hrStats.active_employees}</p>
          </div>
        </div>
        <div className="card p-3 flex flex-col gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100">
            <Users className="w-4 h-4 text-gray-500" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase leading-tight">Inactive</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{hrStats.inactive_employees}</p>
          </div>
        </div>
        <div className="card p-3 flex flex-col gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-100">
            <DollarSign className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase leading-tight">Monthly Payroll</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{formatAmount(hrStats.total_monthly_payroll)}</p>
          </div>
        </div>
        <div className="card p-3 flex flex-col gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-100">
            <DollarSign className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase leading-tight">Paid This Month</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{hrStats.paid_this_month}</p>
          </div>
        </div>
        <div className="card p-3 flex flex-col gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${hrStats.unpaid_this_month > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
            <DollarSign className={`w-4 h-4 ${hrStats.unpaid_this_month > 0 ? 'text-amber-600' : 'text-gray-500'}`} />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase leading-tight">Unpaid</p>
            <p className={`text-xl font-bold mt-0.5 ${hrStats.unpaid_this_month > 0 ? 'text-amber-700' : 'text-gray-900'}`}>{hrStats.unpaid_this_month}</p>
          </div>
        </div>
      </div>
      {/* Secondary HR metrics row */}
      {(parseFloat(hrStats.advance_outstanding) > 0 || hrStats.pending_attendance_summaries > 0) && (
        <div className="flex flex-wrap gap-3 mb-4">
          {parseFloat(hrStats.advance_outstanding) > 0 && (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <DollarSign size={14} className="text-amber-600" />
              <span className="text-amber-700 font-medium">Advances Outstanding:</span>
              <span className="font-bold text-amber-900">{formatAmount(hrStats.advance_outstanding)}</span>
            </div>
          )}
          {hrStats.pending_attendance_summaries > 0 && (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <DollarSign size={14} className="text-blue-600" />
              <span className="text-blue-700 font-medium">Pending Payroll Summaries:</span>
              <span className="font-bold text-blue-900">{hrStats.pending_attendance_summaries}</span>
            </div>
          )}
        </div>
      )}

      {/* Action Button */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="btn-primary flex items-center justify-center gap-2"
        >
          <Plus size={18} />
          <span>Add Employee</span>
        </button>
      </div>

      {/* Search & Filter */}
      <div className="card p-4 mb-6">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by name, phone, CNIC, or designation..."
            className="flex-1 outline-none text-gray-700 bg-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className={`w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                hasActiveFilters
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-gray-50 text-gray-400 cursor-not-allowed'
              }`}
            >
              <RefreshCw size={16} />
              Clear Filters
            </button>
          </div>
        </div>
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-blue-900"><X size={12} /></button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                Status: {statusFilter === 'true' ? 'Active' : 'Inactive'}
                <button onClick={() => setStatusFilter('')} className="hover:text-purple-900"><X size={12} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Count + Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="text-sm text-gray-600">
          {totalCount > 0 ? (
            <>Showing <span className="font-semibold">{startItem}</span>-<span className="font-semibold">{endItem}</span> of <span className="font-semibold">{totalCount.toLocaleString()}</span> employees</>
          ) : (
            <span>No employees found</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            >
              {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button onClick={() => goToPage(1)} disabled={currentPage === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"><ChevronsLeft size={16} /></button>
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"><ChevronLeft size={16} /></button>
              <span className="px-3 py-1 text-sm text-gray-700 font-medium">{currentPage} / {totalPages}</span>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"><ChevronRight size={16} /></button>
              <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600"><ChevronsRight size={16} /></button>
            </div>
          )}
        </div>
      </div>

      {/* Table + Cards */}
      <div className="relative">
        {loading && employees.length > 0 && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        )}

        {/* Desktop Table */}
        <div className="hidden lg:block card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Net Salary</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      <p className="font-medium mb-1">No employees found</p>
                      <p className="text-sm">Try adjusting your search or filters</p>
                      {hasActiveFilters && (
                        <button onClick={clearFilters} className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium">Clear all filters</button>
                      )}
                    </td>
                  </tr>
                ) : (
                  employees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">{emp.name}</div>
                        <div className="text-xs text-gray-500">{emp.username}</div>
                        {emp.device_user_id && <div className="text-xs text-gray-500">Device: {emp.device_user_id}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{emp.phone}</div>
                        {emp.cnic && <div className="text-xs text-gray-500">CNIC: {emp.cnic}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{emp.designation}</div>
                        {emp.department && <div className="text-xs text-gray-500">{emp.department}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{formatAmount(emp.basic_salary)}</div>
                        <div className="text-xs text-gray-500">
                          {emp.allowances > 0 && <span className="text-green-600">+{formatAmount(emp.allowances)}</span>}
                          {emp.advance > 0 && <span className="text-red-600 ml-2">-{formatAmount(emp.advance)}</span>}
                          {emp.deductions > 0 && <span className="text-red-600 ml-2">-{formatAmount(emp.deductions)}</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-blue-700">{formatAmount(emp.net_salary)}</div>
                        {parseFloat(emp.salary_outstanding || 0) > 0 && (
                          <div className="text-xs text-amber-600 font-medium">+{formatAmount(emp.salary_outstanding)} due</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            emp.is_active ? 'badge-success' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {emp.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                          {emp.is_active && (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              emp.salary_paid_this_month
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {emp.salary_paid_this_month ? 'Salary Paid' : 'Unpaid'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => setViewingEmployee(emp)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="View Details"><Eye size={16} /></button>
                          <button onClick={() => setEditingEmployee(emp)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Edit2 size={16} /></button>
                          <button onClick={() => setDeleteConfirm(emp.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {employees.length === 0 ? (
            <div className="card p-12 text-center">
              <p className="font-medium text-gray-900 mb-1">No employees found</p>
              <p className="text-sm text-gray-600">Try adjusting your search or filters</p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium">Clear all filters</button>
              )}
            </div>
          ) : (
            employees.map((emp) => (
              <div key={emp.id} className="card p-4 hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">{emp.name}</p>
                    <p className="text-xs text-gray-500">{emp.username}</p>
                    {emp.device_user_id && <p className="text-xs text-gray-500">Device: {emp.device_user_id}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                      emp.is_active ? 'badge-success' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {emp.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                    {emp.is_active && (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        emp.salary_paid_this_month
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {emp.salary_paid_this_month ? 'Salary Paid' : 'Unpaid'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Phone</p>
                    <p className="text-sm text-gray-900">{emp.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Designation</p>
                    <p className="text-sm text-gray-900">{emp.designation}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Basic Salary</p>
                    <p className="text-sm text-gray-900">{formatAmount(emp.basic_salary)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Net Salary</p>
                    <p className="text-sm font-bold text-blue-700">{formatAmount(emp.net_salary)}</p>
                    {parseFloat(emp.salary_outstanding || 0) > 0 && (
                      <p className="text-xs text-amber-600 font-medium">+{formatAmount(emp.salary_outstanding)} due</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setViewingEmployee(emp)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium">
                    <Eye size={16} /><span>View</span>
                  </button>
                  <button onClick={() => setEditingEmployee(emp)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
                    <Edit2 size={16} /><span>Edit</span>
                  </button>
                  <button onClick={() => setDeleteConfirm(emp.id)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium">
                    <Trash2 size={16} /><span>Delete</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft size={16} />Prev
          </button>
          <span className="text-sm text-gray-600">Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span></span>
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
            Next<ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Employee?</h3>
            <p className="text-sm text-gray-600 mb-6">This action cannot be undone. This will permanently delete the employee record.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <EmployeeModal
          onClose={() => setIsAddModalOpen(false)}
          onSave={() => { setIsAddModalOpen(false); fetchEmployees(currentPage); fetchStats(); }}
        />
      )}

      {/* Edit Modal */}
      {editingEmployee && (
        <EmployeeModal
          employee={editingEmployee}
          onClose={() => setEditingEmployee(null)}
          onSave={() => { setEditingEmployee(null); fetchEmployees(currentPage); fetchStats(); }}
        />
      )}

      {/* View Detail Modal */}
      {viewingEmployee && (
        <EmployeeDetailModal
          employee={viewingEmployee}
          onClose={() => setViewingEmployee(null)}
        />
      )}
    </div>
  );
};

// ─── Employee Form Modal (Add + Edit) ───────────────────────────────────────

const EmployeeModal = ({ employee, onClose, onSave }) => {
  const isEdit = !!employee;
  const [formData, setFormData] = useState({
    user: employee?.user || '',
    name: employee?.name || '',
    phone: employee?.phone || '',
    cnic: employee?.cnic || '',
    address: employee?.address || '',
    designation: employee?.designation || '',
    department: employee?.department || '',
    joining_date: employee?.joining_date || new Date().toISOString().split('T')[0],
    device_user_id: employee?.device_user_id || '',
    basic_salary: employee?.basic_salary?.toString() || '',
    allowances: employee?.allowances?.toString() || '0',
    advance: employee?.advance?.toString() || '0',
    deductions: employee?.deductions?.toString() || '0',
    emergency_contact_name: employee?.emergency_contact_name || '',
    emergency_contact_phone: employee?.emergency_contact_phone || '',
    is_active: employee?.is_active ?? true,
    bonus_eligible: employee?.bonus_eligible ?? true,
    notes: employee?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const netSalary = (
    (parseFloat(formData.basic_salary) || 0) +
    (parseFloat(formData.allowances) || 0) -
    (parseFloat(formData.advance) || 0) -
    (parseFloat(formData.deductions) || 0)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = {
      ...formData,
      basic_salary: parseFloat(formData.basic_salary) || 0,
      allowances: parseFloat(formData.allowances) || 0,
      advance: parseFloat(formData.advance) || 0,
      deductions: parseFloat(formData.deductions) || 0,
    };

    // For edit, don't send user field (it's read-only on update)
    if (isEdit) delete payload.user;

    try {
      if (isEdit) {
        await api.patch(`/employees/${employee.id}/`, payload);
      } else {
        await api.post('/employees/', payload);
      }
      onSave();
    } catch (err) {
      console.error("Error saving employee:", err);
      const detail = err.response?.data;
      const msg = typeof detail === 'object'
        ? Object.entries(detail).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ')
        : "Failed to save employee.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Employee' : 'Add Employee'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          {/* Personal Info */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Personal Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {!isEdit && (
                <div>
                  <label className={labelClass}>User ID (Django User)</label>
                  <input type="text" name="user" value={formData.user} onChange={handleChange} className={inputClass} placeholder="Django User ID" required />
                </div>
              )}
              <div>
                <label className={labelClass}>Full Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="Employee name" required />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="03XX-XXXXXXX" required />
              </div>
              <div>
                <label className={labelClass}>CNIC</label>
                <input type="text" name="cnic" value={formData.cnic} onChange={handleChange} className={inputClass} placeholder="XXXXX-XXXXXXX-X" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Address</label>
                <textarea name="address" value={formData.address} onChange={handleChange} className={inputClass} rows="2" placeholder="Home address" />
              </div>
            </div>
          </div>

          {/* Employment */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Employment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Designation</label>
                <input type="text" name="designation" value={formData.designation} onChange={handleChange} className={inputClass} placeholder="Job title" required />
              </div>
              <div>
                <label className={labelClass}>Department</label>
                <input type="text" name="department" value={formData.department} onChange={handleChange} className={inputClass} placeholder="Department" />
              </div>
              <div>
                <label className={labelClass}>Joining Date</label>
                <input type="date" name="joining_date" value={formData.joining_date} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Biometric Device ID</label>
                <input type="text" name="device_user_id" value={formData.device_user_id} onChange={handleChange} className={inputClass} placeholder="AIKE A700 user ID" />
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} id="is_active" className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Active Employee</label>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input type="checkbox" name="bonus_eligible" checked={formData.bonus_eligible} onChange={handleChange} id="bonus_eligible" className="w-4 h-4 text-green-600 rounded focus:ring-green-500" />
                <label htmlFor="bonus_eligible" className="text-sm font-medium text-gray-700">Eligible for Bonus Pool</label>
              </div>
            </div>
          </div>

          {/* Salary */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Salary Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Basic Salary</label>
                <input type="number" name="basic_salary" value={formData.basic_salary} onChange={handleChange} className={inputClass} placeholder="0" min="0" step="0.01" required />
              </div>
              <div>
                <label className={labelClass}>Allowances</label>
                <input type="number" name="allowances" value={formData.allowances} onChange={handleChange} className={inputClass} placeholder="0" min="0" step="0.01" />
              </div>
              <div>
                <label className={labelClass}>Advance</label>
                <input type="number" name="advance" value={formData.advance} onChange={handleChange} className={inputClass} placeholder="0" min="0" step="0.01" />
              </div>
              <div>
                <label className={labelClass}>Deductions</label>
                <input type="number" name="deductions" value={formData.deductions} onChange={handleChange} className={inputClass} placeholder="0" min="0" step="0.01" />
              </div>
              <div className="sm:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-700 uppercase">Net Salary</p>
                <p className="text-xl font-bold text-blue-900">PKR {netSalary.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Emergency Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Contact Name</label>
                <input type="text" name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange} className={inputClass} placeholder="Name" />
              </div>
              <div>
                <label className={labelClass}>Contact Phone</label>
                <input type="text" name="emergency_contact_phone" value={formData.emergency_contact_phone} onChange={handleChange} className={inputClass} placeholder="Phone" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className={labelClass}>Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} className={inputClass} rows="2" placeholder="Additional notes..." />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {loading && <Loader size={16} className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Add Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Print Salary Slip ───────────────────────────────────────────────────────

const handlePrintSalarySlip = (payment, previousDue = 0) => {
  const prevDue = parseFloat(previousDue || 0);
  const netSalary = parseFloat(payment.basic_salary) + parseFloat(payment.allowances) - parseFloat(payment.advance) - parseFloat(payment.deductions) - parseFloat(payment.attendance_deduction || 0) + parseFloat(payment.bonus_amount || 0) + prevDue;
  const methodLabels = { cash: 'Cash', bank: 'Bank Transfer', jazzcash: 'JazzCash', easypaisa: 'EasyPaisa' };

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Salary Slip - ${payment.employee_name}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; max-width: 700px; margin: 0 auto; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1e40af; padding-bottom: 20px; }
        .header h1 { color: #1e40af; margin: 0; font-size: 28px; }
        .header p { color: #6b7280; margin: 5px 0 0; font-size: 14px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 30px; }
        .info-item label { font-size: 11px; text-transform: uppercase; color: #6b7280; font-weight: 600; letter-spacing: 0.5px; }
        .info-item p { margin: 4px 0 0; font-size: 14px; color: #111827; font-weight: 500; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background: #f3f4f6; text-align: left; padding: 10px 16px; font-size: 12px; text-transform: uppercase; color: #374151; border-bottom: 2px solid #e5e7eb; }
        td { padding: 10px 16px; border-bottom: 1px solid #e5e7eb; font-size: 14px; }
        .text-right { text-align: right; }
        .total-row { font-weight: 700; font-size: 16px; background: #eff6ff; }
        .total-row td { border-top: 2px solid #1e40af; border-bottom: 2px solid #1e40af; }
        .paid-badge { display: inline-block; background: #16a34a; color: white; padding: 8px 24px; border-radius: 6px; font-weight: 700; font-size: 16px; margin-top: 10px; }
        .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 12px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>OpticFlow ISP</h1>
        <p>Salary Slip</p>
      </div>
      <div class="info-grid">
        <div class="info-item"><label>Employee</label><p>${payment.employee_name}</p></div>
        <div class="info-item"><label>Payment Date</label><p>${payment.payment_date}</p></div>
        <div class="info-item"><label>Payment Method</label><p>${methodLabels[payment.payment_method] || payment.payment_method}</p></div>
        <div class="info-item"><label>Slip ID</label><p>#SP-${payment.id}</p></div>
      </div>
      <table>
        <thead><tr><th>Description</th><th class="text-right">Amount (PKR)</th></tr></thead>
        <tbody>
          <tr><td>Basic Salary</td><td class="text-right">${parseFloat(payment.basic_salary).toLocaleString()}</td></tr>
          <tr><td>Allowances</td><td class="text-right">+ ${parseFloat(payment.allowances).toLocaleString()}</td></tr>
          <tr><td>Advance Deduction</td><td class="text-right">- ${parseFloat(payment.advance).toLocaleString()}</td></tr>
          <tr><td>Other Deductions</td><td class="text-right">- ${parseFloat(payment.deductions).toLocaleString()}</td></tr>
          ${parseFloat(payment.attendance_deduction || 0) > 0 ? `<tr><td>Attendance Deduction (${payment.absent_days || 0} absent days)</td><td class="text-right">- ${parseFloat(payment.attendance_deduction || 0).toLocaleString()}</td></tr>` : ''}
          ${parseFloat(payment.bonus_amount || 0) > 0 ? `<tr><td>Bonus</td><td class="text-right" style="color:#16a34a">+ ${parseFloat(payment.bonus_amount || 0).toLocaleString()}</td></tr>` : ''}
          ${prevDue > 0 ? `<tr><td>Previous Month Due</td><td class="text-right" style="color:#b45309">+ ${prevDue.toLocaleString()}</td></tr>` : ''}
          <tr class="total-row"><td>Net Salary</td><td class="text-right">${netSalary.toLocaleString()}</td></tr>
        </tbody>
      </table>
      ${payment.attendance_period_start ? `<p style="font-size: 13px; color: #374151; margin: 0 0 12px;"><strong>Attendance Period:</strong> ${payment.attendance_period_start} to ${payment.attendance_period_end} | Present: ${payment.present_days || 0} | Absent: ${payment.absent_days || 0} | Incomplete: ${payment.incomplete_days || 0}</p>` : ''}
      <div style="text-align: center; margin: 20px 0;">
        <p style="font-size: 13px; color: #6b7280;">Amount Paid</p>
        <p style="font-size: 28px; font-weight: 700; color: #111827;">PKR ${netSalary.toLocaleString()}</p>
        <div class="paid-badge">PAID</div>
      </div>
      ${payment.notes ? `<p style="margin-top: 20px; font-size: 13px; color: #6b7280;"><strong>Notes:</strong> ${payment.notes}</p>` : ''}
      <div class="footer"><p>OpticFlow ISP - Salary Payment Receipt</p></div>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => printWindow.print();
};


// ─── Employee Detail Modal ───────────────────────────────────────────────────

const EmployeeDetailModal = ({ employee, onClose }) => {
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [advances, setAdvances] = useState([]);
  const [loadingAdvances, setLoadingAdvances] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoadingPayments(true);
        const res = await api.get(`/salary-payments/?employee=${employee.id}`);
        setPayments(res.data.results || res.data || []);
      } catch (err) {
        console.error('Error fetching payments:', err);
      } finally {
        setLoadingPayments(false);
      }
    };
    const fetchAdvances = async () => {
      try {
        setLoadingAdvances(true);
        const res = await api.get(`/employee-advances/?employee=${employee.id}`);
        setAdvances(res.data.results || res.data || []);
      } catch (err) {
        console.error('Error fetching advances:', err);
      } finally {
        setLoadingAdvances(false);
      }
    };
    fetchPayments();
    fetchAdvances();
  }, [employee.id]);

  const formatAmount = (v) => `PKR ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-gray-900">Employee Details</h2>
          <div className="flex items-center gap-2">
            <a href="/payroll" className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
              <CreditCard size={15} /> Payroll →
            </a>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><X size={20} /></button>
          </div>
        </div>

        <div className="p-6">
          {/* Personal Info */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-500">Full Name</p><p className="text-sm font-medium text-gray-900">{employee.name}</p></div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-500">Phone</p><p className="text-sm font-medium text-gray-900">{employee.phone}</p></div>
              </div>
              {employee.cnic && (
                <div className="flex items-start gap-3">
                  <CreditCard className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div><p className="text-xs text-gray-500">CNIC</p><p className="text-sm font-medium text-gray-900">{employee.cnic}</p></div>
                </div>
              )}
              {employee.address && (
                <div className="flex items-start gap-3 col-span-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div><p className="text-xs text-gray-500">Address</p><p className="text-sm font-medium text-gray-900">{employee.address}</p></div>
                </div>
              )}
            </div>
          </div>

          {/* Employment */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Employment</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Building className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-500">Designation</p><p className="text-sm font-medium text-gray-900">{employee.designation}</p></div>
              </div>
              {employee.department && (
                <div className="flex items-start gap-3">
                  <Building className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div><p className="text-xs text-gray-500">Department</p><p className="text-sm font-medium text-gray-900">{employee.department}</p></div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-500">Joining Date</p><p className="text-sm font-medium text-gray-900">{employee.joining_date}</p></div>
              </div>
              {employee.device_user_id && (
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div><p className="text-xs text-gray-500">Biometric Device ID</p><p className="text-sm font-medium text-gray-900">{employee.device_user_id}</p></div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                <div><p className="text-xs text-gray-500">Status</p>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${employee.is_active ? 'badge-success' : 'bg-gray-100 text-gray-600'}`}>
                    {employee.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Salary Breakdown</h3>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Basic Salary</span><span className="font-medium">{formatAmount(employee.basic_salary)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Allowances</span><span className="font-medium text-green-600">+ {formatAmount(employee.allowances)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Advance</span><span className="font-medium text-red-600">- {formatAmount(employee.advance)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Deductions</span><span className="font-medium text-red-600">- {formatAmount(employee.deductions)}</span></div>
                <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between"><span className="font-bold text-gray-900">Net Salary</span><span className="font-bold text-blue-700 text-lg">{formatAmount(employee.net_salary)}</span></div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${employee.bonus_eligible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {employee.bonus_eligible ? 'Bonus Eligible' : 'Excluded from Bonus'}
                </span>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          {(employee.emergency_contact_name || employee.emergency_contact_phone) && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Emergency Contact</h3>
              <div className="grid grid-cols-2 gap-4">
                {employee.emergency_contact_name && (
                  <div><p className="text-xs text-gray-500">Name</p><p className="text-sm font-medium text-gray-900">{employee.emergency_contact_name}</p></div>
                )}
                {employee.emergency_contact_phone && (
                  <div><p className="text-xs text-gray-500">Phone</p><p className="text-sm font-medium text-gray-900">{employee.emergency_contact_phone}</p></div>
                )}
              </div>
            </div>
          )}

          {/* Advance History */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Advance History</h3>
            {loadingAdvances ? (
              <div className="flex items-center justify-center py-6"><Loader className="w-5 h-5 text-yellow-600 animate-spin" /></div>
            ) : advances.length === 0 ? (
              <p className="text-sm text-gray-500 py-3 text-center">No advance records yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {advances.map((a) => (
                      <tr key={a.id} className="hover:bg-yellow-50/40">
                        <td className="px-4 py-2">{a.date}</td>
                        <td className="px-4 py-2 text-right font-medium text-yellow-700">{formatAmount(a.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div>
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Payment History</h3>
            {loadingPayments ? (
              <div className="flex items-center justify-center py-8"><Loader className="w-6 h-6 text-blue-600 animate-spin" /></div>
            ) : payments.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No salary payments yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Method</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Salary</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Bonus</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {payments.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{p.payment_date}</td>
                        <td className="px-4 py-2 capitalize">{p.payment_method}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatAmount(p.amount)}</td>
                        <td className="px-4 py-2 text-right">
                          {parseFloat(p.bonus_amount || 0) > 0
                            ? <span className="font-medium text-green-600">+ {formatAmount(p.bonus_amount)}</span>
                            : <span className="text-gray-300">—</span>
                          }
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button onClick={() => handlePrintSalarySlip(p)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Print Slip">
                            <Printer size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Notes */}
          {employee.notes && (
            <div className="mt-6">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">Notes</h3>
              <p className="text-sm text-gray-700">{employee.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default Employees;
